import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import i18n from 'i18n';
import { cgToast } from 'modules/Common/ui/toast';
import { trackFreeSupplierFirstDataAdded } from 'helpers/mixpanel';
import { initialMockData } from './mockData';
import { computeGaps, computeReadiness } from './gaps';
import { deriveSupplierChecklist } from './checklist';
import useCnGoodLabel from './useCnGoodLabel';
import {
  operatorToCompany,
  companyToOperatorPayload,
  installationToLite,
  liteToInstallationPayload,
  requestToLite,
} from './adapters';
import { supplierFactorsToFlatFactors } from 'modules/FreeSupplier/supplierFactorReadModel';
import {
  useFetchLoggedInOperator,
  useCreateOperator,
  useUpdateOperator,
  useCreateOperatorInstallation,
  useUpdateOperatorInstallation,
  useLiteInstallations,
  useLiteRequests,
  useLiteRequestMutations,
} from './liteHooks';
import { useSupplierInbox } from 'modules/FreeSupplier/useSupplierInbox';
import { useSupplierFactors } from 'modules/FreeSupplier/useSupplierFactors';
import { useSupplierUploads } from 'modules/FreeSupplier/useSupplierUploads';
import { useSupplierDrafts } from 'modules/FreeSupplier/useSupplierDrafts';
import { useSupplierPublicationSettings } from 'modules/FreeSupplier/useSupplierPublication';

// The /supplier free-tier journey runs on LIVE operator data. This provider reads
// and writes the same endpoints the full operator app uses (operators,
// operator installations, operator emissions, the S8 Supplier Page publication
// settings, connection requests) and adapts them into the flat view-model the
// /supplier pages + gap engine consume (see adapters.js).
//
// Only purely-cosmetic UI state stays client-side (localStorage): dismissed
// upsells, the widget-builder config, and a marker for the last template
// upload. Everything else is a real API call.
//
// D-S9-3: `share` is a READ-ONLY flat projection of the S8 publication
// settings (useSupplierPublicationSettings — the SAME react-query cache
// SharePortalPage reads/writes directly) for cross-cutting consumers that only
// need `enabled`/`slug` (DiagramViewPage, the getting-started checklist). The
// legacy /operator/cbam-portal/* wiring (useCbamPortalStatus/
// useConfigureCbamPortal/useDisableCbamPortal) is REMOVED from the supplier
// surface entirely — SharePortalPage owns the write path via
// useUpdateSupplierPublication/useRegenerateSupplierPublicationSlug.

const MockDataContext = createContext(null);

const PREFS_KEY = 'cg-lite-prefs-v1';

function loadPrefs() {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

export function MockDataProvider({ children }) {
  // ---- live data reads ---------------------------------------------------
  const { data: operator, isLoading: operatorLoading } = useFetchLoggedInOperator();
  const { docs: installationDocs, isLoading: installationsLoading } = useLiteInstallations();
  // The S8 Supplier Page publication settings — same react-query cache
  // SharePortalPage reads/writes directly (D-S9-3).
  const { data: publicationSettings, isPending: publicationPending } =
    useSupplierPublicationSettings();
  // Item 16B: real CN-good labels for computeGaps' `default_data` message —
  // see gaps.js's `labelFor` doc comment.
  const cnGoodLabel = useCnGoodLabel();
  const { incomingDeclarants, incomingOperators, outgoing } = useLiteRequests(true);
  // Incoming declarant requests come from the S3 supplier inbox (the connection/
  // request model). Its query is shared with the Data Requests page via react-query,
  // and it drives the getting-started "respond to a request" step + the nav badge.
  const { data: supplierInbox, isPending: inboxPending } = useSupplierInbox();
  // Getting-started checklist truth for "has factors" / "template uploaded" —
  // the S5/S6 versioned surfaces, not the legacy /emissions/operator read.
  const { data: supplierFactors, isPending: factorsPending } = useSupplierFactors();
  const { data: supplierUploads, isPending: uploadsPending } = useSupplierUploads();
  // Checklist truth for "add values as drafts" / "discard a draft" — the
  // same drafts list the Review & Drafts nav badge and DraftsInboxPage read.
  const { data: supplierDrafts, isPending: draftsPending } = useSupplierDrafts();

  // ---- live data writes --------------------------------------------------
  const createOperator = useCreateOperator();
  const updateOperator = useUpdateOperator();
  const createInstallation = useCreateOperatorInstallation();
  const updateInstallationMut = useUpdateOperatorInstallation();
  const requestMut = useLiteRequestMutations();

  // ---- client-only cosmetic state ---------------------------------------
  const [prefs, setPrefs] = useState(loadPrefs);
  const widget = prefs.widget || initialMockData.widget;
  const dismissedUpsells = prefs.dismissedUpsells || [];

  const patchPrefs = useCallback((patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch };
      try {
        window.localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — cosmetic state just won't persist */
      }
      return next;
    });
  }, []);

  // ---- editable drafts (form fields the pages mutate keystroke-by-keystroke)
  // company + share are seeded from live data once, then edited locally and
  // flushed back to the API on save / toggle.
  const [companyDraft, setCompanyDraft] = useState(null);
  const seededCompanyId = useRef(null);
  useEffect(() => {
    if (operatorLoading) return;
    const id = operator?._id || '__none__';
    if (seededCompanyId.current !== id) {
      seededCompanyId.current = id;
      setCompanyDraft(operatorToCompany(operator));
    }
  }, [operator, operatorLoading]);

  // D-S9-3: `share` below is a read-only flat projection of the S8 publication
  // settings (publicationSettings, fetched above) for cross-cutting consumers
  // that only need enabled/slug. Writes live in SharePortalPage via
  // useUpdateSupplierPublication — never here.

  // ---- derived flat view-model ------------------------------------------
  const company = companyDraft || operatorToCompany(operator);

  const installations = useMemo(() => installationDocs.map(installationToLite), [installationDocs]);

  // B1: the SAME versioned source (and the SAME mapping) the factors page and
  // checklist already use — never the legacy installationDocs[].emissionsFactors
  // arrays. One canonical read model for every consumer (gaps, readiness,
  // installation summaries, portal preview, share surfaces).
  const emissionFactors = useMemo(
    () => supplierFactorsToFlatFactors(supplierFactors?.items),
    [supplierFactors],
  );

  const share = useMemo(
    () => ({
      enabled: Boolean(publicationSettings?.enabled),
      slug: publicationSettings?.slug ?? null,
    }),
    [publicationSettings],
  );

  const requests = useMemo(
    () => [
      ...incomingDeclarants.map((r) =>
        requestToLite(r, { direction: 'incoming', type: 'Declarant' }),
      ),
      ...incomingOperators.map((r) =>
        requestToLite(r, { direction: 'incoming', type: 'Operator' }),
      ),
      ...outgoing.map((r) => requestToLite(r, { direction: 'outgoing', type: 'Operator' })),
    ],
    [incomingDeclarants, incomingOperators, outgoing],
  );

  const data = useMemo(
    () => ({
      company,
      installations,
      emissionFactors,
      share,
      requests,
      widget,
      dismissedUpsells,
    }),
    [company, installations, emissionFactors, share, requests, widget, dismissedUpsells],
  );

  // ---- company actions ---------------------------------------------------
  const updateCompany = useCallback((patch) => {
    setCompanyDraft((c) => ({ ...(c || {}), ...patch }));
  }, []);

  const saveCompany = useCallback(async () => {
    const payload = companyToOperatorPayload(companyDraft || company, operator);
    try {
      if (operator?._id) {
        await updateOperator.mutateAsync(payload);
      } else {
        await createOperator.mutateAsync(payload);
      }
      // Force a re-seed from the freshly-persisted record.
      seededCompanyId.current = null;
      cgToast.success(i18n.t('freeSupplier:toasts.savedTitle', 'Saved'), {
        description: i18n.t('freeSupplier:toasts.profileSavedBody', 'Company profile saved'),
      });
    } catch (err) {
      cgToast.error('Error', {
        description: err?.response?.data?.error || 'Failed to save company profile',
      });
    }
  }, [companyDraft, company, operator, updateOperator, createOperator]);

  // ---- installation actions ---------------------------------------------
  const addInstallation = useCallback(
    async (form) => {
      const isFirst = installations.length === 0;
      try {
        await createInstallation.mutateAsync(liteToInstallationPayload(form));
        if (isFirst) trackFreeSupplierFirstDataAdded({ kind: 'installation' });
      } catch {
        /* the create hook already surfaces a notification */
      }
    },
    [createInstallation, installations.length],
  );

  const updateInstallation = useCallback(
    async (id, form) => {
      const existing = installationDocs.find((d) => String(d._id) === String(id));
      try {
        await updateInstallationMut.mutateAsync({
          installationId: id,
          installationData: liteToInstallationPayload(form, existing),
        });
      } catch {
        /* the update hook already surfaces a notification */
      }
    },
    [installationDocs, updateInstallationMut],
  );

  // ---- request actions ---------------------------------------------------
  const respondToRequest = useCallback(
    async (id, status) => {
      const req = requests.find((r) => r.id === id);
      const isDeclarant = req?.type === 'Declarant';
      try {
        if (status === 'accepted') {
          await requestMut.accept.mutateAsync({ id, isDeclarant });
          cgToast.success(i18n.t('freeSupplier:toasts.acceptedTitle', 'Accepted'), {
            description: i18n.t(
              'freeSupplier:toasts.shareAcceptedBody',
              'Your data is now shared with them',
            ),
          });
        } else {
          await requestMut.reject.mutateAsync({ id, isDeclarant });
        }
      } catch (err) {
        cgToast.error('Error', {
          description: err?.response?.data?.message || 'Failed to update request',
        });
      }
    },
    [requests, requestMut],
  );

  const sendRequest = useCallback(
    async (payload) => {
      // The lite form collects company + email + goods + period. The operator
      // connection endpoint (POST /requests/operator) now carries goods +
      // reportingPeriod and treats country as optional, so send them through.
      const goods = Array.isArray(payload.goods)
        ? payload.goods.filter(Boolean)
        : payload.goods
          ? [payload.goods]
          : [];
      try {
        await requestMut.send.mutateAsync({
          recieverName: payload.company || payload.email || '',
          recieverEmail: payload.email || '',
          goods,
          reportingPeriod: payload.period || '',
          // country is optional on this path — only send when the form has one.
          ...(payload.country ? { country: payload.country } : {}),
        });
        cgToast.success(i18n.t('freeSupplier:toasts.sentTitle', 'Sent'), {
          description: i18n.t('freeSupplier:toasts.requestSentBody', 'Data request sent'),
        });
      } catch (err) {
        cgToast.error('Error', {
          description: err?.response?.data?.message || 'Failed to send request',
        });
      }
    },
    [requestMut],
  );

  // ---- widget / upsell (client-only) ------------------------------------
  const updateWidget = useCallback(
    (patch) => patchPrefs({ widget: { ...widget, ...patch } }),
    [patchPrefs, widget],
  );

  const dismissUpsell = useCallback(
    (key) => {
      if (dismissedUpsells.includes(key)) return;
      patchPrefs({ dismissedUpsells: [...dismissedUpsells, key] });
    },
    [patchPrefs, dismissedUpsells],
  );

  // ---- assembled context value ------------------------------------------
  const value = useMemo(() => {
    // Incoming declarant requests, flattened from the S3 inbox connections.
    const s3Requests = (supplierInbox?.items || []).flatMap((connection) => connection.requests);
    // D-15 (fixpack #15): the full 9-step checklist derivation lives in the
    // pure helper below so it's unit-testable without this context — see
    // checklist.js / checklist.test.js.
    const checklist = deriveSupplierChecklist({
      companySaved: data.company.saved,
      installationCount: data.installations.length,
      emissionFactors: data.emissionFactors,
      factorsSummary: supplierFactors?.summary,
      uploads: supplierUploads?.items,
      drafts: supplierDrafts?.items,
      portalEnabled: data.share.enabled,
      requests: s3Requests,
    });
    const completedSteps = Object.values(checklist).filter(Boolean).length;
    const pendingRequestCount = s3Requests.filter((request) => request.state === 'pending').length;
    const gaps = computeGaps(data, { labelFor: cnGoodLabel });
    const readinessScore = computeReadiness(gaps);

    return {
      data,
      // Raw operator + installation docs, exposed so pages can reuse the real
      // operator forms/modals (which seed from the real document shape) instead
      // of hand-rolled forms — see D48 (reuse-first).
      operator,
      installationDocs,
      loading: operatorLoading || installationsLoading,
      // `loading` above covers only the operator + installation reads. The
      // checklist is derived from FOUR more queries (factors, uploads, drafts,
      // inbox) plus the publication settings, and every one of them defaults to
      // "not done" while pending — so a page gated on `loading` alone still
      // paints a confident all-unticked checklist and then corrects itself.
      // Gate the getting-started page on this instead.
      checklistLoading:
        operatorLoading ||
        installationsLoading ||
        factorsPending ||
        uploadsPending ||
        draftsPending ||
        inboxPending ||
        publicationPending,
      checklist,
      completedSteps,
      totalSteps: Object.keys(checklist).length,
      pendingRequestCount,
      gaps,
      readinessScore,
      updateCompany,
      saveCompany,
      addInstallation,
      updateInstallation,
      respondToRequest,
      sendRequest,
      updateWidget,
      dismissUpsell,
    };
  }, [
    data,
    supplierInbox,
    supplierFactors,
    supplierUploads,
    supplierDrafts,
    operator,
    installationDocs,
    operatorLoading,
    installationsLoading,
    factorsPending,
    uploadsPending,
    draftsPending,
    inboxPending,
    publicationPending,
    cnGoodLabel,
    updateCompany,
    saveCompany,
    addInstallation,
    updateInstallation,
    respondToRequest,
    sendRequest,
    updateWidget,
    dismissUpsell,
  ]);

  return <MockDataContext.Provider value={value}>{children}</MockDataContext.Provider>;
}

export function useLiteData() {
  const ctx = useContext(MockDataContext);
  if (!ctx) throw new Error('useLiteData must be used within MockDataProvider');
  return ctx;
}

// Back-compat alias: the /supplier pages were written against useMockData(). The
// shape is identical, so both names resolve to the same live-data context.
export const useMockData = useLiteData;
